// @ts-check
// Envio de modelo aprovado do WhatsApp numa conversa (contrato 08): com a janela de 24 horas fechada é a única forma
// de voltar a falar com a família. Escolhe o modelo, preenche as variáveis e mostra a prévia do texto; quando o modelo
// certo não existe, o atalho "Criar modelo" leva à tela de modelos com este número (contrato 08, seção 7).
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { sendTemplateMessage } from '../../data/integrations.js';
import { currentWorkspaceMemberState } from '../../data/metadata-store.js';
import { Modal } from '../../ui/modal.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { FormError, describeError } from '../settings/integration-ui.js';
import { CreateTemplateShortcut } from '../settings/whatsapp-templates/create-template-shortcut.js';
import { isTemplateReady, suggestTemplateValues, trimTemplateValues } from '../../lib/whatsapp-templates.js';
import { TemplatePicker, useMarketingBlock, useSendableTemplates } from './template-picker.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/integrations.js').WhatsappTemplate} WhatsappTemplate
 * @typedef {import('../../data/conversations.js').ConversationThread} ConversationThread
 */

/**
 * Nome do contato da conversa: texto na API e nome composto no modo demonstração.
 * @param {ConversationThread} thread
 */
function threadContactName(thread) {
  const personName = /** @type {any} */ (thread.person)?.name;
  return typeof personName === 'string' ? personName : personName?.firstName ?? thread.contact?.displayName ?? '';
}

/**
 * @param {{ thread: ConversationThread, onClose: () => void, onSent: () => void }} props
 */
export function TemplateMessageModal({ thread, onClose, onSent }) {
  const templates = useSendableTemplates(thread.channelId);
  const marketingBlock = useMarketingBlock(thread.person?.id ?? null);
  const [selectedId, setSelectedId] = useState('');
  const [values, setValues] = useState(/** @type {Record<string, string>} */ ({}));
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSending, setIsSending] = useState(false);
  const selected = templates?.find((template) => template.id === selectedId) ?? null;

  /** @param {string} templateId */
  const select = (templateId) => {
    setSelectedId(templateId);
    const template = templates?.find((candidate) => candidate.id === templateId);
    setValues(template ? suggestTemplateValues(template, { contactName: threadContactName(thread), memberName: currentWorkspaceMemberState.value?.name?.firstName }) : {});
    setError(null);
  };
  const send = async () => {
    if (!selected) return;
    setIsSending(true);
    setError(null);
    try {
      await sendTemplateMessage(thread.id, { templateId: selected.id, variables: trimTemplateValues(values) });
      showSuccessSnackbar(t('leadCapture.templateMessage.sentSnackbar'));
      onSent();
    } catch (requestError) {
      setError(describeError(requestError));
    } finally {
      setIsSending(false);
    }
  };

  return html`<${Modal} title=${t('leadCapture.templateMessage.title')} subtitle=${t('leadCapture.templateMessage.subtitle')}
    confirmLabel=${t('leadCapture.templateMessage.send')} isConfirmDisabled=${!isTemplateReady(selected, values)} isLoading=${isSending} onClose=${onClose} onConfirm=${send}>
    <div class="integration-modal-body">
      <${TemplatePicker} templates=${templates} selectedId=${selectedId} onSelect=${select} values=${values} onValuesChange=${setValues} marketingBlock=${marketingBlock} />
      <${FormError} message=${error} />
      <${CreateTemplateShortcut} channelId=${thread.channelId} onBeforeNavigate=${onClose} prefix=${t('whatsappTemplates.shortcut.notFound')}
        memberText=${t('whatsappTemplates.shortcut.pickerMemberNote')} className="template-picker-shortcut" />
    </div>
  <//>`;
}
